<template>
  <b-modal
    v-model="show"
    id="modifierModal"
    centered
    :title="actionDisplay"
    @shown="open"
    @hidden="clear"
  >
    <b-form class="create-tax-form">
      <info-row class="info-main-row" :title="'Name'">
        <b-col class="input-group">
          <b-form-input
            v-model="taxName"
            class="form-control"
            type="text"
            title="tax Name"
            placeholder="Liquor Tax"
            required
            :state="fieldState.taxName"
          />
        </b-col>
      </info-row>

      <info-row class="info-main-row" :title="'Description'">
        <b-col class="input-group">
          <b-form-input
            v-model="description"
            class="form-control"
            type="text"
            title="Description"
            placeholder="Appled to all alcoholic beverages"
            required
            :state="fieldState.description"
          />
        </b-col>
      </info-row>

      <info-row class="info-main-row" :title="'Calculation'">
        <b-col class="input-group type-select">
          <b-form-select
            v-model="calculation"
            title="Calculation"
            required
            :state="fieldState.calculation"
            :options="calculations"
          />
        </b-col>
      </info-row>

      <info-row class="info-main-row" :title="'Type'">
        <b-col class="input-group type-select">
          <b-form-select
            v-model="modifierType"
            title="Type"
            required
            :state="fieldState.calculation"
            :options="modifierTypes"
          />
        </b-col>
      </info-row>

      <info-row class="info-main-row" :title="'Rate'">
        <currency-input
          v-if="show"
          ref="rateInput"
          name="Default Rate"
          :currencySymbol="calculation === modifierRateTypes.FIXED ? '$' : '%'"
          :amount.sync="defaultRate"
          :fieldState="fieldState.defaultRate"
        />
      </info-row>

      <Taggregator
        ref="tagsTaggregator"
        name="tags"
        class="mt-4"
        label="Applied To:"
        labelKey="name"
        :items="getTags"
        :selections.sync="selectedTags"
        :minimum="0"
        :quickAddCallback="quickAddTag"
        :quickAddMinWordCount="1"
        :quickAddMinWordLength="1"
        :quickAddDescription="'Please provide valid item name'"
        :onAdd="addModifierTag"
        :onRemove="removeModifierTag"
        :quickAddHasUniqueName="true"
      >
        <template slot="result" slot-scope="{result}">
          <span class="result-label">
            {{ result.name }}
          </span>
        </template>
        <template slot="selection" slot-scope="{selection}">
          {{ selection.name }}
        </template>
      </Taggregator>
    </b-form>

    <div slot="modal-footer" class="w-100">
      <span class="text-danger">{{ warningText }}</span>
      <b-btn v-if="tax.id" class="float-left" variant="danger" @click="deleteTax">
        Delete Tax
      </b-btn>
      <b-btn class="float-right" variant="success" @click="triggerTax">
        {{ actionDisplay }}
      </b-btn>
    </div>

    <div v-if="isRequesting" class="spinner-loading">
      <spinner-loader />
    </div>
  </b-modal>
</template>

<script>
  import { mapActions, mapState } from 'vuex';

  import {
    PAYMENT_TYPES,
    PAYMENT_METHODS,
    MODIFIER_TYPES,
    MODIFIER_RATE_TYPES,
  } from '@/config/constants'


  export default {
    name: 'EntertaxModal',

    components: {
      CurrencyInput: () => import('@/components/widgets/CurrencyInput'),
      InfoRow: () => import('@/components/InfoRow'),
      PicturePlaceholder: () => import('@/components/widgets/PicturePlaceholder'),
      SpinnerLoader: () => import('@/components/widgets/SpinnerLoader'),
      Taggregator: () => import('@/components/Taggregator'),
    },

    data() {
      return {
        tax: {},
        selectedTags: [],
        actionDisplay: 'Create Tax',
        show: null,
        taxName: '',
        calculation: null,
        modifierType: null,
        modifierRateTypes: MODIFIER_RATE_TYPES,
        defaultRate: '',
        description: null,
        warningText: "",
        hideAfter: true,
        fieldState: { //true is valid, false shows the red invalid
          defaultRate: null,
          taxName: null,
          description: null,
          calculation: null,
          modifierType: null
        }
      }
    },

    computed: {
      ...mapState({
        isRequesting: state => state.tags.isRequesting,
        getTags: state => state.tags.tags
      }),

      calculations() {
        return Object.keys(MODIFIER_RATE_TYPES)
          .map(calculation => {
            return {
              value: MODIFIER_RATE_TYPES[calculation],
              text: calculation.toLowerCase()
            }
          })
      },

      modifierTypes() {
        return Object.keys(MODIFIER_TYPES)
          .map(modifierType => {
            return {
              value: MODIFIER_TYPES[modifierType],
              text: modifierType.toLowerCase()
            }
          })
      }
    },

    created() {
      this.fetchTags()
    },

    methods: {
      ...mapActions([
        'fetchTags',
        'createModifierTag',
        'deleteModifierTag',
        'deleteModifierAndModifierTags'
      ]),

      open(tax = {}) {
        if (!tax.id) {
          return //for some reason, the button event gets called here sometimes.
        }

        this.tax = tax;
        this.actionDisplay = 'Update Tax';

        this.taxId = tax.id;
        this.taxName = tax.name;
        this.description = tax.description;
        this.defaultRate = parseFloat(tax.default_rate);
        this.calculation = tax.modifier_type_id;
        this.modifierType = tax.category_id;

        if (tax.modifier_tag && tax.modifier_tag.length > 0) {
          this.postProcesstaxTags(tax);
        }

        this.show = true
      },

      postProcesstaxTags(tax) {
        tax.modifier_tag.forEach(modTag => {
          const foundTag = this.getTags.find(tag => modTag.tag_id === tag.id)

          if (foundTag) {
            this.selectedTags.push({
              ...foundTag,
              link_id: modTag.id
            })
          }
        });
      },

      async addModifierTag(tag) {
        if (!this.tax.id) {
          return // For new modifiers, we create this on save
        }

        const newModifierTag = await this.createModifierTag({tag_id: tag.id, modifier_id: this.tax.id});

        //So when it gets deleted, it has it's modifier_tag_id to do it
        this.selectedTags
          .find(selectedTag => selectedTag.id === tag.id).link_id = newModifierTag.id;

        this.infoNotification(`${tag.name} added to ${this.taxName}`)
      },

      removeModifierTag(tag) {
        if (!this.tax.id || !tag.link_id) {
          return // Only need to tell the DB if it's in there
        }

        this.deleteModifierTag(tag.link_id);
        this.infoNotification(`${tag.name} removed from ${this.taxName}`)
      },

      triggerTax() {
        this.fieldState.defaultRate = !!this.defaultRate || this.defaultRate === 0
        this.fieldState.description = !!this.description
        this.fieldState.taxName = !!this.taxName && this.taxName.length > 1
        this.fieldState.calculation = !!this.calculation
        this.fieldState.modifierType = !!this.modifierType

        if (
          this.fieldState.defaultRate &&
          this.fieldState.description &&
          this.fieldState.taxName &&
          this.fieldState.calculation &&
          this.fieldState.modifierType
        ) {
          this.submitTax();
        } else {
          this.warningText = 'Name, Default Rate, Type, calculation and Description are all required.'
        }
      },

      async deleteTax() {
        if (this.tax.is_locked) {
          this.errorNotification('This item is locked, so it can not be deleted');
          return;
        }

        if (this.tax.is_used) {
          this.errorNotification('This item is used for the bookings, so it can not be deleted');
          return;
        }

        let deletedModifier = {};

        try {
          deletedModifier = await this.deleteModifierAndModifierTags(this.tax.id)

        } catch (e) {
          this.errorNotification('Error deleting tax: ' + e);
          return
        }

        this.infoNotification(`Deleted ${deletedModifier.name} and all tag links.`)

        this.clear()
        this.show = false;

        this.$emit("delete")
      },

      submitTax() {
        const taxData = {
          id: this.tax.id,
          name: this.taxName,
          description: this.description,
          default_rate: this.defaultRate,
          tax_tag: this.selectedTags,
          is_locked: this.tax.is_locked || 0,
          category_id: this.modifierType,
          modifier_type_id: this.calculation,
        }

        this.$emit('submit', taxData)

        if (true === this.hideAfter) {
          this.clear()
          this.show = false;
        }
      },

      quickAddTag(name, taxs) {
        this.$store.dispatch('createTag', { name })
          .then((response) => {
            taxs.push(response);
            this.successNotification('Tag succesfully created!');
          })
          .catch((err) => {
            this.errorNotification('Failed to create tag! :(')
          });
      },

      clear() {
        Object.assign(this.$data, this.$options.data.apply(this))
      }
    }
  };
</script>

<style lang="scss">
#modifierModal {
  top: 30px;
}

.spinner-loading {
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0px;
  top: 0px;
  background: rgba(255,255,255,0.7);
}

.input-group.type-select.col select {
  text-transform: capitalize;
}
</style>